/**
 * Has two other variants - scroll and group
 */
import { Spacing } from '@persona-id/design-tokens';
import { MouseEvent } from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import EditorNavItem, {
  EditorNavItemBase,
} from 'dashboard/apps/InquiryTemplateEditor/EditorNavItem';
import { ClickableItemProps } from 'dashboard/components/ClickableItem';
import { PrefixAndLabelContainer } from 'dashboard/components/ClickableItem/ClickableItem';
import Tooltip from 'design-system/components/Tooltip';
import menuItemMixins from 'design-system/mixins/menuItem';
import { paragraphMixins } from 'design-system/mixins/text';

// shared active styling for LinkItem and ScrollItem
export const linkActiveCSS = css`
  color: ${({ theme }) => theme.color.primary.brand};

  &.active > * {
    ${menuItemMixins.active}
  }
`;

export const editorLinkCSS = css`
  ${paragraphMixins.md}
  margin-top: ${Spacing[4]};
  cursor: pointer;

  ${EditorNavItemBase} {
    width: 100%;
  }

  ${PrefixAndLabelContainer} {
    display: inline-block;
    width: 100%;
    text-align: start;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-right: ${Spacing[3]};
  }

  &.active > * {
    ${linkActiveCSS}
  }
`;

const InternalLink = styled(NavLink)`
  ${editorLinkCSS}
`;

const TooltipContentContainer = styled.div`
  max-width: 230px;
  overflow-wrap: break-word;
  text-align: center;
  padding: 0 ${Spacing[2]};
`;

interface EditorNavItemLinkProps
  extends Pick<
    ClickableItemProps,
    'className' | 'disabled' | 'isVisible' | 'prefixIcon' | 'suffixIcon' | 'variant'
  > {
  id: string;
  label: string;
  disabledLabel?: string;
  route?: NavLinkProps['to'];
  dataTest?: string;
  exact?: boolean;
  onClick?: (e: MouseEvent) => void;
}

/**
 * editor nav item - internal link variant
 */
const EditorNavItemLink = ({
  id,
  dataTest,
  className,
  label,
  disabledLabel,
  route,
  isVisible,
  disabled,
  exact = true,
  prefixIcon,
  suffixIcon,
  onClick,
}: EditorNavItemLinkProps) => {
  const dataTestId = dataTest ?? `editor_navitem__${id}`;
  const item = (
    <EditorNavItem
      id={dataTestId}
      data-test={dataTestId}
      disabled={disabled}
      isVisible={isVisible}
      prefixIcon={prefixIcon}
      suffixIcon={suffixIcon}
      onClick={onClick}
    >
      {label}
    </EditorNavItem>
  );

  return route != null && !disabled ? (
    <InternalLink end={exact} to={route} className={className}>
      {item}
    </InternalLink>
  ) : (
    /**
     * defaults to ClickableItem with wrapper if no route provided,
     * such as when used as the parent container in EditorNavItemGroup
     */
    <Tooltip
      side='right'
      sideOffset={22}
      trigger={<div className={className}>{item}</div>}
      disabled={disabledLabel == null}
    >
      <TooltipContentContainer>{disabledLabel}</TooltipContentContainer>
    </Tooltip>
  );
};

export default EditorNavItemLink;
